import {
    Box,
    Divider,
    Flex,
    Heading,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { AspectRatio } from "@chakra-ui/react";
import { AvailabilityBadge } from "components/availability-badge";
import { SafeHTML } from "components/safe-html";
import ImageWithSkeleton from "components/selection-components/image-with-skeleton/image-with-skeleton";
import { useLocalizedValue } from "helpers/use-localized-value";
import { urlBuilder } from "search-params/url-builder";
import { ExperienceViewItem, ExperienceViewItemType } from "types/experience-view";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { AvailabilityState } from "utils/availability-utils";

type ExperienceViewItemLayout = "section" | "experience-paired" | "experience-full";

interface ExperienceViewItemCardProps {
    item: ExperienceViewItem;
    layout: ExperienceViewItemLayout;
    isLastItem?: boolean;
    renderOnlyExperience?: boolean;
    availabilityState?: AvailabilityState | null;
    onClick?: () => void;
}

const hoverStyle = { boxShadow: "var(--theme-shadows-outline)" };
const focusStyle = { boxShadow: "var(--theme-shadows-outline)" };

function ExperienceViewItemCard({
    item,
    layout,
    isLastItem,
    renderOnlyExperience,
    availabilityState,
    onClick,
}: ExperienceViewItemCardProps) {
    const localizedName = useLocalizedValue(
        item.type === ExperienceViewItemType.Experience ? item.name : undefined
    );
    const localizedDescription = useLocalizedValue(
        item.type === ExperienceViewItemType.Experience ? item.description : undefined
    );

    if (item.type === ExperienceViewItemType.Section) {
        const baseUrl = item.imageReference1x1
            ? `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${item.imageReference1x1}`
            : "";
        const imageUrl = baseUrl
            ? urlBuilder(baseUrl, { AspectRatio: ImageServiceAspectRatio.OneByOne, Type: "webp" })
            : "";

        return (
            <Box
                as="button"
                w="full"
                type="button"
                onClick={onClick}
                // @ts-expect-error _focus not yet typed in casper-ui-library Box
                _focus={focusStyle}
                _hover={hoverStyle}
            >
                <Flex
                    justify="space-between"
                    py={ThemeSpaceVariable.Medium}
                    align="center"
                    gap={ThemeSpaceVariable.Small}
                    flex={1}
                >
                    <Stack spacing={ThemeSpaceVariable.None}>
                        <Heading
                            as="h3"
                            noOfLines={1}
                            fontFamily={ThemeFontVariable.SubHeader}
                            fontWeight="medium"
                            color={ThemeColorVariable.OnBackgroundHeader}
                            fontSize={{
                                base: ThemeFontSizeVariable.Large,
                                md: ThemeFontSizeVariable.XLarge,
                            }}
                        >
                            {item.name}
                        </Heading>
                        {item.description && (
                            <Text
                                noOfLines={1}
                                fontSize={{
                                    base: ThemeFontSizeVariable.Small,
                                    md: ThemeFontSizeVariable.Large,
                                }}
                                maxW="380px"
                            >
                                <SafeHTML
                                    element="span"
                                    options={{ allowedTags: ["br"], allowedSchemes: [] }}
                                    html={item.description}
                                />
                            </Text>
                        )}
                        <AvailabilityBadge
                            state={availabilityState}
                            mt={ThemeSpaceVariable.XSmall}
                        />
                    </Stack>
                    {item.imageReference1x1 && (
                        <ImageWithSkeleton
                            src={imageUrl}
                            alt={item.name}
                            maxH="72px"
                            skeletonWidth="72px"
                            skeletonHeight="72px"
                            borderRadius={ThemeSpaceVariable.Small}
                        />
                    )}
                </Flex>
                <Divider />
            </Box>
        );
    }

    // Experience cards: image on top, text below, 2-line wrap
    const isFull = layout === "experience-full";
    const imageRef = isFull ? item.imageReference16x9 : item.imageReference1x1;
    const aspectRatio = isFull
        ? ImageServiceAspectRatio.SixteenByNine
        : ImageServiceAspectRatio.OneByOne;
    const baseUrl = imageRef ? `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${imageRef}` : "";
    const imageUrl = baseUrl ? urlBuilder(baseUrl, { AspectRatio: aspectRatio, Type: "webp" }) : "";

    // Show a divider below no-image items only:
    // - Never when the item has an image (image acts as visual separator)
    // - In the experiences-only tab (renderOnlyExperience=true): always, including the last item
    // - In the show-all tab (renderOnlyExperience=false): only for non-last items, because
    //   sections always render a divider on top when they follow experiences
    const showDivider = !imageRef && (!isLastItem || renderOnlyExperience);

    const DESCRIPTION_LINE_COUNT = 2;
    const DESCRIPTION_LINE_HEIGHT = 1.5;
    return (
        <>
            <Box
                as="button"
                w="full"
                type="button"
                textAlign="left"
                onClick={onClick}
                // @ts-expect-error _focus not yet typed in casper-ui-library Box
                _focus={focusStyle}
                _hover={hoverStyle}
            >
                <Stack
                    spacing={ThemeSpaceVariable.None}
                    overflow="hidden"
                    py={ThemeSpaceVariable.Medium}
                >
                    {imageRef && (
                        <AspectRatio ratio={isFull ? 16 / 9 : 1} mb={ThemeSpaceVariable.Small}>
                            <ImageWithSkeleton
                                src={imageUrl}
                                alt={localizedName}
                                skeletonWidth="100%"
                                skeletonHeight="100%"
                                w="full"
                                h="full"
                                objectFit="cover"
                                borderRadius={ThemeSpaceVariable.Small}
                            />
                        </AspectRatio>
                    )}
                    <Heading
                        as="h3"
                        noOfLines={1}
                        fontFamily={ThemeFontVariable.SubHeader}
                        fontWeight="medium"
                        color={ThemeColorVariable.OnBackgroundHeader}
                        fontSize={{
                            base: ThemeFontSizeVariable.Large,
                            md: ThemeFontSizeVariable.XLarge,
                        }}
                    >
                        {localizedName}
                    </Heading>
                    {localizedDescription && (
                        // Safari bug workaround: -webkit-line-clamp (used by noOfLines) causes
                        // CSS Grid to size rows using the unclamped text height, ignoring the
                        // visual clip. The fix is to take the Text out of normal flow (position
                        // absolute) so it cannot inflate the grid row. This Box reserves the
                        // correct space: height = DESCRIPTION_LINE_COUNT × DESCRIPTION_LINE_HEIGHT em,
                        // fontSize is set here (not on the child), "em" resolves against this
                        // element's font size and scales correctly at every breakpoint.
                        <Box
                            position="relative"
                            height={`${DESCRIPTION_LINE_COUNT * DESCRIPTION_LINE_HEIGHT}em`}
                            lineHeight={DESCRIPTION_LINE_HEIGHT}
                            fontSize={{
                                base: ThemeFontSizeVariable.Small,
                                md: ThemeFontSizeVariable.Large,
                            }}
                        >
                            <Text
                                fontSize="inherit"
                                noOfLines={DESCRIPTION_LINE_COUNT}
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                            >
                                <SafeHTML
                                    element="span"
                                    options={{ allowedTags: ["br"], allowedSchemes: [] }}
                                    html={localizedDescription}
                                />
                            </Text>
                        </Box>
                    )}
                    <AvailabilityBadge state={availabilityState} mt={ThemeSpaceVariable.XSmall} />
                </Stack>
            </Box>
            {showDivider && <Divider />}
        </>
    );
}

export { ExperienceViewItemCard };
